import React, {useEffect, useRef, useState} from 'react';
import {ChevronRightIcon, ChevronUpIcon, XIcon} from '@heroicons/react/outline';
import {trackEvent} from 'src/utils/tracking';
import {
  NormalizedDataset,
  deleteDataset,
  editDataset,
  responseToDataset,
} from 'src/models/dataset';
import {DatasetStatusEnum} from 'src/enums/datasetstatusenum';
import _ from 'lodash';
import {useMutation, useQueryClient} from 'react-query';
import {useAxios} from 'src/utils/http';
import {ToastMessage} from 'src/core/components/toast';
import {toast} from 'react-toastify';
import {AxiosError} from 'axios';
import {Modal} from 'src/core/layout/modal';
import {CreateDatasetForm} from 'src/components/create-dataset-form';
import {Loading} from '../../../core/components/loading';
import {
  HiEllipsisVertical,
  HiOutlinePencil,
  HiOutlineTrash,
} from 'react-icons/hi2';
import {useProjectContext} from 'src/models/project-context';
import {ProjectDatasetCard} from 'src/components/dataset/project-dataset-card';
import {ProjectWithDatasets} from 'src/models/dataset';
import {useNavigate, Link} from 'react-router-dom';
import {HiOutlineSearch, HiOutlineTerminal} from 'react-icons/hi';

function getButtonStates(
  isGhosted?: boolean,
  isLocked?: boolean,
  isShared?: boolean
) {
  const ghosted = !!isGhosted;
  const locked = !!isLocked;
  const shared = !!isShared;

  if (ghosted || locked) {
    return {
      disableSearch: true,
      disableAddDataset: true,
    };
  }

  if (shared) {
    return {
      disableSearch: false,
      disableAddDataset: true,
    };
  }

  return {
    disableSearch: false,
    disableAddDataset: false,
  };
}

interface ProjectCardProps {
  projectId: string;
  projectName: string;
  isLocked?: boolean;
  isShared?: boolean;
  isGhosted?: boolean;
  onClose: () => void;
  onToggleLock: () => void;
  onRename: () => void;
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  onExpand?: (name: string | null) => void;
  datasets?: NormalizedDataset[];
  isInternalUser: boolean;
  datasetsLoading?: boolean;
  allProjects?: ProjectWithDatasets[];
  onDatasetMoved?: () => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  projectId,
  projectName,
  isLocked,
  isShared,
  isGhosted,
  onClose,
  onToggleLock,
  onRename,
  children,
  defaultExpanded = false,
  onExpand,
  datasets = [],
  // isInternalUser,
  datasetsLoading = false,
  allProjects = [],
  onDatasetMoved,
}) => {
  const api = useAxios();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const queryClient = useQueryClient();
  const {setProjectId} = useProjectContext();
  const {disableSearch, disableAddDataset} = getButtonStates(
    isGhosted,
    isLocked,
    isShared
  );

  const [showProjectActions, setShowProjectActions] = useState(false);
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalType, modalTypeChange] = useState<
    'create' | 'edit' | 'request' | 'requestConfirmed' | 'delete' | 'archive'
  >();
  const [modalOpen, modalOpenChange] = useState(false);
  const [modalDataset, modalDatasetChange] = useState<any>();
  const [localDatasets, setLocalDatasets] =
    useState<NormalizedDataset[]>(datasets);

  useEffect(() => {
    setLocalDatasets(datasets);
  }, [datasets]);

  const editDatasetMut = useMutation(
    ({datasetId, name}: {datasetId: number; name: string}) =>
      editDataset(api, datasetId, name),
    {
      onSuccess: async response => {
        trackEvent('CLICK_EDIT_DATASET', {
          project_id: projectId,
        });
        const updated = responseToDataset(response.data);
        setLocalDatasets(prev =>
          prev.map(ds =>
            ds.id === updated.id ? {...ds, name: updated.name} : ds
          )
        );
        await queryClient.invalidateQueries(['projectsByOwner']);
        toast(<ToastMessage title="Dataset updated" icon="success" />);

        closeModal();
      },
      onError: (err: AxiosError) => {
        const message =
          (err as any)?.response?.data?.message ?? 'Error updating dataset';
        toast(<ToastMessage title={message} icon="error" />);
      },
    }
  );

  const deleteDatasetMut = useMutation(
    ({datasetId}: {datasetId: number}) => deleteDataset(api, datasetId),
    {
      onSuccess: async (response, {datasetId}) => {
        trackEvent('CLICK_DELETE_DATASET', {
          project_id: projectId,
        });
        setLocalDatasets(prev => prev.filter(ds => ds.id !== datasetId));
        await queryClient.invalidateQueries(['projectsByOwner']);
        toast(<ToastMessage title="Dataset deleted" icon="success" />);

        closeModal();
      },
      onError: (err: AxiosError) => {
        const message =
          (err as any)?.response?.data?.message ?? 'Error deleting dataset';
        toast(<ToastMessage title={message} icon="error" />);
      },
    }
  );

  const openModal = (
    type:
      | 'create'
      | 'edit'
      | 'request'
      | 'requestConfirmed'
      | 'delete'
      | 'archive',
    dataset?: NormalizedDataset
  ) => {
    // @todo: Don't use empty dataset for create modal
    const emptyDataset: NormalizedDataset = {
      id: 0,
      userId: 0,
      name: '',
      status: DatasetStatusEnum.DatasetStatusDatasetCreated,
      includeUndetermined: true,
      favorite: true,
      dicomViewingProcessing: false,
    };

    modalTypeChange(type);
    modalDatasetChange(_.cloneDeep(dataset || emptyDataset));
    modalOpenChange(true);
  };

  const closeModal = () => {
    modalOpenChange(false);
    modalTypeChange(undefined);
    modalDatasetChange(undefined);
  };

  const renderDatasets = () => {
    if (datasetsLoading) {
      return <Loading text="Loading datasets..." />;
    }

    if (localDatasets.length === 0) {
      return (
        <div className="text-center text-gray-500 py-6">
          No datasets available
        </div>
      );
    }

    return (
      <div className="min-w-full space-y-4">
        {/* Headers */}
        <div className="grid grid-cols-[2fr_1fr_1.5fr_1.5fr_1.5fr_1fr_1fr_0.5fr] items-center px-4 py-2 border-b bg-gray-100 font-medium text-gray-700 text-sm">
          <div className="truncate">Dataset Name</div>
          <div className="truncate">Dataset ID</div>
          <div className="truncate">Owner</div>
          <div className="truncate">Status</div>
          <div className="truncate">Created At</div>
          <div className="truncate">Study Count</div>
          <div className="truncate">DICOMS</div>
          <div className="truncate text-right">Actions</div>
        </div>
        {localDatasets.map((dataset, i) => (
          <ProjectDatasetCard
            key={i}
            dataset={dataset}
            action={actionType => {
              switch (actionType) {
                case 'edit':
                case 'archive':
                case 'delete':
                  openModal(actionType, dataset);
                  break;
                case 'request':
                  // do nothing
                  break;
              }
            }}
            projectID={projectId}
            isGhosted={isGhosted}
            isLocked={isLocked}
            allProjects={allProjects}
            onDatasetMoved={onDatasetMoved}
          />
        ))}
      </div>
    );
  };

  const renderEditModal = (dataset: NormalizedDataset) => {
    return (
      <div>
        <div className="text-lg mb-3">Rename Dataset</div>
        <div className="mb-3">
          <label htmlFor="name" className="input-label">
            New name
          </label>
          <input
            id="name"
            type="text"
            className="text-input w-full mt-2"
            value={dataset.name}
            autoComplete="off"
            onChange={e => {
              dataset.name = e.target.value;
              modalDatasetChange(_.cloneDeep(dataset));
            }}
          />
        </div>
        <div className="flex flex-row-reverse">
          <button
            className="btn btn-primary"
            onClick={() => {
              editDatasetMut.mutate({
                datasetId: dataset.id,
                name: dataset.name,
              });
            }}
            disabled={editDatasetMut.isLoading}
          >
            Save
          </button>
        </div>
      </div>
    );
  };

  const renderDeleteModal = (dataset: NormalizedDataset) => {
    return (
      <div>
        <div className="text-lg mb-3">Delete Dataset</div>
        <div className="text-sm text-gray-500 mb-3">
          All reports and tags saved to this dataset will be lost.
        </div>
        <div className="flex flex-row-reverse">
          <button
            className="btn btn-danger"
            onClick={() => {
              deleteDatasetMut.mutate({datasetId: dataset.id});
            }}
            disabled={deleteDatasetMut.isLoading}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as any).contains(event.target)
      ) {
        setShowProjectActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onSearchClick = () => {
    navigate(`/project/${projectId}/search`);
  };

  const onTerminalSearch = () => {
    navigate(`/project/${projectId}/terminal`);
  };

  const handleButtonClick = () => {
    setShowSearchPopup(prevState => !prevState);
  };

  return (
    <div
      className="bg-white border-t border-gray-200 hover:shadow-md transition-shadow"
      title={isLocked ? 'Closed Project' : 'Open Project'}
      data-id={projectId}
    >
      <div
        className={`flex justify-between items-center px-4 py-3 ${
          isGhosted ? 'bg-gray-100' : 'none'
        }`}
      >
        <div className="flex items-center space-x-2 text-lg font-semibold">
          <button
            onClick={() => {
              setProjectId(projectId);
              onExpand?.(defaultExpanded ? null : projectName);
            }}
            aria-label="Toggle project section"
          >
            {defaultExpanded ? (
              <ChevronUpIcon
                className={`h-5 w-5 ${
                  isGhosted ? 'text-gray-500' : 'text-black'
                }`}
              />
            ) : (
              <ChevronRightIcon
                className={`h-5 w-5 ${
                  isGhosted ? 'text-gray-500' : 'text-black'
                }`}
              />
            )}
          </button>
          <Link
            to={`/project/${projectId}`}
            onClick={() => {
              trackEvent('CLICK_PROJECT_NAME', {project_id: projectId});
              setProjectId(projectId);
            }}
            data-cy="ProjectCard_projectName"
            className={`text-sm font-medium hover:underline ${
              isGhosted ? 'text-gray-400' : 'text-blue-600 hover:text-blue-800'
            }`}
          >
            {projectName}
          </Link>
        </div>

        <div className="flex items-center space-x-6">
          <div className="relative">
            {' '}
            <button
              className={`text-blue-600 text-sm hover:text-blue-800 ${
                disableSearch ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={() => {
                trackEvent('CLICK_SEARCH_BTN', {project_id: projectId});
                if (!disableSearch) {
                  setProjectId(projectId);
                  handleButtonClick();
                }
              }}
              disabled={disableSearch}
            >
              Search
            </button>
            {/* Popup Menu */}
            {showSearchPopup && (
              <ul className="absolute left-0 z-10 mt-2 w-48 bg-white border rounded shadow-lg text-sm">
                <li>
                  <button
                    onClick={() => {
                      setShowSearchPopup(false);
                      onSearchClick();
                    }}
                    className="w-full items-center px-4 py-2 text-left hover:bg-gray-100"
                  >
                    <HiOutlineSearch className="w-5 h-5 inline" />{' '}
                    <span className="ml-2">Search Page</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setShowSearchPopup(false);
                      onTerminalSearch();
                    }}
                    className="w-full items-center px-4 py-2 text-left hover:bg-gray-100"
                  >
                    <HiOutlineTerminal className="w-5 h-5 inline" />{' '}
                    <span className="ml-2">Terminal Search</span>
                  </button>
                </li>
              </ul>
            )}
          </div>
          <button
            className={`text-blue-600 text-sm hover:text-blue-800 ${
              disableAddDataset ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={() => {
              trackEvent('CLICK_CREATE_DATASET_BTN', {project_id: projectId});
              if (!disableAddDataset) {
                setProjectId(projectId);
                openModal('create');
              }
            }}
            disabled={disableAddDataset}
          >
            Add Dataset
          </button>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => {
                if (!isShared) {
                  setProjectId(projectId);
                  setShowProjectActions(prev => !prev);
                }
              }}
              className={`p-2 hover:bg-gray-100 rounded-full ${
                isShared ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="More actions"
            >
              <HiEllipsisVertical className="w-6 h-6 text-gray-600" />
            </button>

            {showProjectActions && (
              <ul className="absolute right-0 z-10 mt-2 w-48 bg-white border rounded shadow-lg text-sm">
                <li>
                  <button
                    onClick={() => {
                      setShowProjectActions(false);
                      onRename();
                    }}
                    className="w-full items-center px-4 py-2 text-left hover:bg-gray-100"
                  >
                    <HiOutlinePencil className="w-5 h-5 inline" />{' '}
                    <span className="ml-2">Rename</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setShowProjectActions(false);
                      onToggleLock();
                    }}
                    className="w-full items-center px-4 py-2 text-left hover:bg-gray-100"
                  >
                    <XIcon className="w-5 h-5 inline" />{' '}
                    <span className="ml-2">{isLocked ? 'Open' : 'Close'}</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      setShowProjectActions(false);
                      setConfirmDeleteProject(true);
                    }}
                    className="w-full items-center px-4 py-2 text-left text-red-500 hover:bg-gray-100"
                  >
                    <HiOutlineTrash className="w-5 h-5 inline" />{' '}
                    <span className="ml-2">Delete</span>
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {defaultExpanded && (
        <>
          {children && <div className="divide-y">{children}</div>}
          {!loading && <div>{renderDatasets()}</div>}
        </>
      )}
      <Modal
        isOpen={modalOpen}
        onRequestClose={() => closeModal()}
        className="w-96 max-w-lg"
        hideExitButton={_.includes(['requestConfirmed'], modalType)}
      >
        {modalType === 'edit' && renderEditModal(modalDataset!)}
        {modalType === 'delete' && renderDeleteModal(modalDataset!)}
        {modalType === 'create' && (
          <CreateDatasetForm
            allowCSVUpload
            closeModal={closeModal}
            setLoading={setLoading}
            projectID={projectId}
            created={(newDs: NormalizedDataset) => {
              setLocalDatasets(ds => [newDs, ...ds]);
            }}
          />
        )}
      </Modal>
      <Modal
        isOpen={confirmDeleteProject}
        onRequestClose={() => setConfirmDeleteProject(false)}
        className="w-96 max-w-lg"
      >
        <div className="text-lg mb-3">Delete Project</div>
        <div className="text-sm text-gray-500 mb-3">
          Are you sure you want to delete this project? This action cannot be
          undone.
        </div>
        <div className="flex flex-row-reverse space-x-2 space-x-reverse">
          <button
            className="btn btn-danger"
            onClick={() => {
              onClose();
              setConfirmDeleteProject(false);
            }}
          >
            Delete
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setConfirmDeleteProject(false)}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};
